import { useState } from 'react';
import { useParams } from 'react-router-dom';
import useStampRallyData from '../../hooks/useStampRallyData';
import { DEFAULT_CAMPAIGN_ID, LP_CONFIG } from '../../constants/lpConfig';
import PageLayout from '../layout/PageLayout';
import Header from '../layout/Header';
import SpotList from './SpotList';
import LastUpdated from './LastUpdated';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * StampRallyPage - スタンプラリーLPページ全体コンポーネント
 *
 * レイヤー構造（下から順）:
 *   1. PageLayout  … サイト共通背景 (bg.png)、固定・最下層
 *   2. 地図背景    … キャンペーン固有 (map-bg.png)、固定・z-0
 *   3. コンテンツ  … Header / MainVisual / SpotList 等、z-10
 *
 * bg-fixed はモバイルで無効なため、fixed + inset-0 の div で背景を敷く。
 */
const StampRallyPage = () => {
  const { campaignId = DEFAULT_CAMPAIGN_ID } = useParams();

  // 有効なキャンペーンIDでなければ diary.rural.ne.jp へリダイレクト
  // ※ Hooks のルール上、この分岐は全 Hooks より前に置く必要があるため
  //   useParams のみ先行して呼び出し、残りのロジックは StampRallyPageContent に委譲する
  if (!LP_CONFIG[campaignId]) {
    window.location.replace('https://diary.rural.ne.jp/');
    return null;
  }

  return <StampRallyPageContent campaignId={campaignId} />;
};

/** StampRallyPage の実体。campaignId が有効なことが保証された状態で呼ばれる */
const StampRallyPageContent = ({ campaignId }) => {
  const { spots, updatedAt, gasLastUpdated, loading, error } = useStampRallyData(campaignId);
  const [mainVisualError, setMainVisualError] = useState(false);

  // ueno: 静的サイトに合わせた表示順・非表示スポット（?????など）は除外
  const UENO_SPOT_NAME_ORDER = [
    '上野動物園',
    '上野駅',
    '不忍池',
    '上野アメ横商店街',
    'アキバ・インフォ',
    '新宿マルイ メン',
  ];

  // katsurahama: ?????（スポットID 13）は座標未設定のため除外
  const KATSURAHAMA_SPOT_NAME_ORDER = [
    '桂浜水族館',
    '桂浜',
    '桂浜水族館 HAMASHUI KITCHEN',
    '高知県立のいち動物公園',
    '高知空港',
    'はりまや橋',
    '?????',
  ];

  const KASAI_SPOT_NAME_ORDER = [
    '有楽町マルイ',
    '日比谷公園　噴水広場',
    '築地川銀座公園',
    '葛西臨海水族園',
    'ラムサール条約湿地銘板',
    '夢の島熱帯植物館',
  ];

  const SPOT_NAME_ORDERS = {
    ueno: UENO_SPOT_NAME_ORDER,
    katsurahama: KATSURAHAMA_SPOT_NAME_ORDER,
    kasairinkai: KASAI_SPOT_NAME_ORDER,
  };

  const nameOrder = SPOT_NAME_ORDERS[campaignId];
  const displaySpots = nameOrder
    ? nameOrder
        .map((name) => spots.find((s) => s.name === name))
        .filter(Boolean)
    : spots;

  const mainVisualPath = `/images/rallies/${campaignId}/rally-visual.png`;
  const mapBgPath = `/images/rallies/${campaignId}/map-bg.png`;

  return (
    <PageLayout>
      {/* map-bg.pngの下地となるピンク背景（390pxカラム内のみ） */}
      <div
        className="sticky top-0 z-0 w-full"
        style={{ backgroundColor: '#ffc5cb', height: '100dvh' }}
        aria-hidden="true"
      />
      {/* 地図背景画像（ピンクの上に重ねる） */}
      <div
        className="sticky top-0 z-0 w-full bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${mapBgPath})`, height: '100dvh', marginTop: '-100dvh' }}
        aria-hidden="true"
      />

      {/* コンテンツ（地図の上に被せて表示） */}
      <main className="relative z-10" style={{ marginTop: '-100dvh' }}>
        <Header />

        {/* メインビジュアル */}
        <div className="px-8 pt-4">
          {!mainVisualError ? (
            <img
              src={mainVisualPath}
              alt="スタンプラリー"
              className="w-full"
              onError={() => setMainVisualError(true)}
            />
          ) : (
            <div className="w-full aspect-[16/9] bg-white rounded-xl flex flex-col items-center justify-center">
              <span className="text-4xl mb-2">🎪</span>
              <span className="text-pink-600 font-bold text-lg">スタンプラリー</span>
              <span className="text-pink-400 text-xs mt-1">メインビジュアル準備中</span>
            </div>
          )}
        </div>

        {/* 達成報酬プレゼント */}
        <div className="px-4 mt-4">
          <img
            src={`/images/rallies/${campaignId}/reward-banner.png`}
            alt="達成報酬プレゼント"
            className="w-full"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>

        {/* みんなの力で〜達成すると */}
        <div className="px-4 mt-4">
          <img
            src={`/images/rallies/${campaignId}/reward-detail.png`}
            alt="みんなの力で全6スポット全て250スタンプ期間中に達成すると"
            className="w-full"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>

        {/* エラーバナー（キャッシュ表示時） */}
        {error && displaySpots.length > 0 && (
          <div className="mx-4 mt-4 bg-yellow-50/90 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-700">
              ⚠️ データの取得に失敗しました。最後に取得したデータを表示しています。
            </p>
            {updatedAt && (
              <p className="text-xs text-yellow-600 mt-1">
                最終更新: {updatedAt.toLocaleString('ja-JP')}
              </p>
            )}
          </div>
        )}

        {/* ローディング */}
        {loading && <LoadingSpinner />}

        {/* 初回取得失敗 */}
        {!loading && error && displaySpots.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <p className="text-gray-600 text-center text-sm">
              ⚠️ データの取得に失敗しました。
              <br />
              ページを再読み込みしてください。
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-pink-500 text-white rounded-lg text-sm hover:bg-pink-600 transition-colors"
            >
              再読み込み
            </button>
          </div>
        )}

        {/* スポット一覧 */}
        {!loading && displaySpots.length > 0 && (
          <>
            {/* スポット別の達成状況 */}
            <div className="px-4 mt-4">
              <img
                src={`/images/rallies/${campaignId}/spot-status-header.png`}
                alt="スポット別の達成状況"
                className="w-full"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>

            {/* スタンプ説明 */}
            <div className="text-center mt-3 space-y-1">
              <p className="text-base font-bold text-gray-800">無料のスポットカード＝1スタンプ</p>
              <p className="text-base font-bold text-gray-800">有料のスポットカード＝2スタンプ</p>
            </div>

            {/* GAS更新時刻（gasLastUpdated優先、なければupdatedAtで代替） */}
            <LastUpdated updatedAt={gasLastUpdated || updatedAt} />

            <SpotList spots={displaySpots} campaignId={campaignId} />
            
            <div className='w-[100%] h-[150px]'></div>
          </>
        )}
      </main>
    </PageLayout>
  );
};

export default StampRallyPage;
